import okaoAxios from './axios'
import { infoLog, debugLog, errorLog } from '../utils/log-helper'

export interface CheckoutUrlResponse {
  checkoutUrl: string
}

export interface PortalUrlResponse {
  url: string
}

export type PlanName = 'free' | 'explorer' | 'vip'

export interface CreateCheckoutRequest {
  planName: PlanName
  eaterId?: string | null
  referrerEaterId?: string | null
  referrerRestaurantId?: string | null
  restaurantId?: string | null
}

/**
 * Creates a Stripe checkout session via the proxy /billing/checkout endpoint
 * @param planName The subscription plan name ('free' | 'explorer' | 'vip')
 * @param eaterId Optional eater ID for eater subscriptions
 * @param referrerEaterId Optional referrer eater ID for referral tracking
 * @param referrerRestaurantId Optional referrer restaurant ID for referral tracking
 * @param restaurantId Optional restaurant ID for restaurant subscriptions
 * @returns Promise resolving to the checkout URL
 */
export async function createCheckoutSession(
  planName: PlanName,
  eaterId?: string | null,
  referrerEaterId?: string | null,
  referrerRestaurantId?: string | null,
  restaurantId?: string | null,
): Promise<string> {
  const start = performance.now()
  infoLog('[API] createCheckoutSession start', {
    url: '/billing/checkout',
    method: 'POST',
    params: {
      planName,
      eaterId: eaterId ?? null,
      restaurantId: restaurantId ?? null,
      hasReferrerEaterId: !!referrerEaterId,
      hasReferrerRestaurantId: !!referrerRestaurantId,
    },
  })
  
  try {
    const body: CreateCheckoutRequest = {
      planName,
      ...(eaterId && { eaterId }),
      ...(referrerEaterId && { referrerEaterId }),
      ...(referrerRestaurantId && { referrerRestaurantId }),
      ...(restaurantId && { restaurantId }),
    }
    const response = await okaoAxios.post<CheckoutUrlResponse>(
      '/billing/checkout',
      body,
    )
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] createCheckoutSession success', { status: response.status, durationMs, planName })
    return response.data.checkoutUrl
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] createCheckoutSession failed', { status: error.response?.status, durationMs, planName, error })
    throw error
  }
}

/**
 * Creates a Stripe Customer Portal session via the proxy /api/billing/portal-session endpoint
 * @returns Promise resolving to the portal URL
 */
export async function createBillingPortalSession(): Promise<string> {
  const start = performance.now()
  infoLog('[API] createBillingPortalSession start', { url: '/api/billing/portal-session', method: 'POST' })
  
  try {
    const response = await okaoAxios.post<PortalUrlResponse>(
      '/api/billing/portal-session',
    )
    const durationMs = Math.round(performance.now() - start)
    debugLog('[API] createBillingPortalSession success', { status: response.status, durationMs })
    return response.data.url
  } catch (error: any) {
    const durationMs = Math.round(performance.now() - start)
    errorLog('[API] createBillingPortalSession failed', { status: error.response?.status, durationMs, error })
    throw error
  }
}

